import type {
  FormDefinitionLike,
  FormDefinitionSectionLike,
  SectionType,
  TaxReturnCell
} from './types';
import { strategiesType } from './types';

interface Values<
  TFormDefinition extends FormDefinitionLike = FormDefinitionLike,
  TPeriod = object
> {
  formVersionId: string;
  form: any;
  def: TFormDefinition;
  taxpayerId: string;
  period?: TPeriod;
  amendedTaxReturnId?: string;
}

export const createTaxReturnRequest = <TRequest>({
  form,
  def,
  formVersionId,
  taxpayerId,
  period,
  amendedTaxReturnId
}: Partial<Values>): TRequest => {
  const cells = (def?.sections ?? []).flatMap((section) =>
    (
      (form?.sections?.[section.code]?.instances ?? []) as Array<{
        sectionRowId?: string | null;
        cells?: Record<string, unknown>;
      }>
    ).flatMap((instance) =>
      section.cells.map((cell) => ({
        code: Number(cell.code),
        value: normalizeCellValue(cell.type, instance.cells?.[cell.code]),
        type: cell.type,
        cellPurpose: cell.cellPurpose,
        sectionRowId: isSimpleSection(section) ? section.code : instance.sectionRowId
      }))
    )
  ) as Array<TaxReturnCell>;

  const valuePeriod = (period ?? {}) as object;

  return {
    formVersionId,
    cells,
    taxpayerId,
    amendedTaxReturnId,
    ...valuePeriod
  } as TRequest;
};

export const createDefaultValesTaxReturn = (
  def: FormDefinitionLike,
  readOnly: boolean,
  cells: Array<TaxReturnCell>,
  jurisdictions: Array<{ id: string }> = []
) => {
  const sections = Object.fromEntries(
    def.sections.map((section) => {
      const byInstance = new Map<string, Array<TaxReturnCell>>();
      cells
        .filter((cell) => section.cells.some((definition) => Number(definition.code) === cell.code))
        .forEach((cell) => {
          const key = isSimpleSection(section) ? section.code : (cell.sectionRowId ?? 'legacy');
          byInstance.set(key, [...(byInstance.get(key) ?? []), cell]);
        });

      const instances = Array.from(byInstance.entries()).map(([id, instanceCells]) =>
        createSectionInstance(section, instanceCells, id === 'legacy' ? createUuidV7() : id)
      );
      const minimum =
        section.sectionType === 'multiple'
          ? (section.effectiveMinInstances ?? section.minInstances ?? 0)
          : 1;

      if (!readOnly && instances.length === 0 && section.sectionType === 'jurisdiction') {
        jurisdictions.forEach((jurisdiction) =>
          instances.push(createSectionInstance(section, [], jurisdiction.id))
        );
      } else if (!readOnly) {
        while (instances.length < minimum) instances.push(createSectionInstance(section));
      }

      return [section.code, { instances }];
    })
  );

  return { sections };
};

export const createSectionInstance = (
  section: FormDefinitionSectionLike,
  values: Array<TaxReturnCell> = [],
  sectionRowId?: string
) => ({
  sectionRowId: isSimpleSection(section) ? section.code : (sectionRowId ?? createUuidV7()),
  cells: Object.fromEntries(
    section.cells.map((cell) => {
      const value = values.find((candidate) => candidate.code === Number(cell.code))?.value;
      return [cell.code, normalizeCellValue(cell.type, value)];
    })
  )
});

const isSimpleSection = (section: FormDefinitionSectionLike) =>
  section.sectionType !== 'multiple' && section.sectionType !== 'jurisdiction';

type LegacySectionType = 'Simple' | 'Multiple' | 'Jurisdiction';

interface FormDefinitionResponseLike {
  sections: Array<
    FormDefinitionSectionLike & {
      provisioningType?: LegacySectionType;
    }
  >;
}

const sectionTypes: Record<LegacySectionType, SectionType> = {
  Simple: 'simple',
  Multiple: 'multiple',
  Jurisdiction: 'jurisdiction'
};

export const normalizeFormDefinition = <T extends FormDefinitionResponseLike>(
  definition: T | undefined
): T | undefined => {
  if (!definition) return undefined;

  return {
    ...definition,
    sections: definition.sections.map(({ provisioningType, sectionType, ...section }) => ({
      ...section,
      sectionType: sectionType ?? (provisioningType ? sectionTypes[provisioningType] : 'simple')
    }))
  } as T;
};

const normalizeCellValue = (type: string, value: unknown) => {
  if (value !== undefined)
    return type === strategiesType.currency ? Number(value).toFixed(2) : value;
  if (type === strategiesType.numeric) return 0;
  if (type === strategiesType.currency) return Number(0).toFixed(2);
  if (type === strategiesType.list) return null;
  if (type === strategiesType.text) return '';
  return undefined;
};

export const createUuidV7 = () => {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let timestamp = Date.now();
  for (let index = 5; index >= 0; index--) {
    bytes[index] = timestamp & 0xff;
    timestamp = Math.floor(timestamp / 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x70;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
